import React, { useEffect, useState } from 'react';
import { Search, ShieldCheck, Loader2, MapPin, Briefcase, Building2, GraduationCap, Users, Star, Calendar, X, CheckCircle2, FileText } from 'lucide-react';
import PoPShell from './PoPShell';
import { popDirectory, ROLES, ENGAGEMENT_ROUTES, roleLabel, popRatingSummary, popListSlots, popBookSlot, popResumeDownloadUrl } from '../../lib/popApi';
import { usePoPAuth } from '../../context/PoPAuthContext';

const ROLE_ICONS = {
  industry_expert: Briefcase,
  university: Building2,
  faculty: Users,
  student: GraduationCap,
  industry_org: Building2,
  super_admin: ShieldCheck,
};

const VerificationBadge = ({ status }) => {
  if (status !== 'verified') return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">
      <ShieldCheck className="w-3 h-3" /> Verified
    </span>
  );
};

const RatingChip = ({ userId }) => {
  const [r, setR] = useState(null);
  useEffect(() => { popRatingSummary('user', userId).then(setR).catch(() => {}); }, [userId]);
  if (!r || !r.count) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-300 bg-amber-300/10 border border-amber-300/30 px-1.5 py-0.5 rounded" title={`${r.count} rating${r.count === 1 ? '' : 's'}`}>
      <Star className="w-3 h-3 fill-amber-300" /> {r.average}
    </span>
  );
};

const BookSlotModal = ({ expert, onClose, onBooked }) => {
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(true);
  const [question, setQuestion] = useState('');
  const [chosen, setChosen] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const r = await popListSlots({ expert_user_id: expert.user_id, upcoming_only: true });
        setSlots(r.results || []);
      } finally { setBusy(false); }
    })();
  }, [expert.user_id]);

  const doBook = async () => {
    if (!chosen) return;
    setConfirming(true); setErr('');
    try {
      await popBookSlot(chosen, question);
      setMsg('Session request sent · expert will confirm');
      onBooked && onBooked();
      setTimeout(() => onClose(), 1500);
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not book'); }
    finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="book-slot-modal">
      <div className="relative max-w-lg w-full rounded-2xl border border-white/10 bg-slate-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1">BOOK A 1:1 SESSION</div>
        <div className="text-lg font-serif font-semibold text-white leading-tight mb-4">with {expert.name}</div>

        {busy ? (
          <div className="flex items-center justify-center py-8 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading slots…</div>
        ) : slots.length === 0 ? (
          <div className="text-sm text-white/60 italic py-4">This expert has no open slots right now. Try messaging them directly.</div>
        ) : (
          <>
            <div className="space-y-1.5 mb-4 max-h-52 overflow-y-auto">
              {slots.map((s) => (
                <button key={s.id} onClick={() => setChosen(s.id)}
                  data-testid={`slot-select-${s.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md border transition ${
                    chosen === s.id ? 'bg-amber-300/10 border-amber-300/40' : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-white/85 font-medium">{new Date(s.starts_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
                      <div className="text-[10px] text-white/50">{s.topic || 'General mentorship'} · {s.duration_minutes} min</div>
                    </div>
                    {chosen === s.id && <CheckCircle2 className="w-4 h-4 text-amber-300" />}
                  </div>
                </button>
              ))}
            </div>
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={3}
              placeholder="What would you like to discuss?" data-testid="slot-question"
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 mb-2" />
            {err && <div className="text-xs text-rose-300 mb-2">{err}</div>}
            {msg && <div className="text-xs text-emerald-300 mb-2">{msg}</div>}
            <button onClick={doBook} disabled={!chosen || confirming} data-testid="slot-book-confirm"
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-40 text-slate-900 text-sm font-semibold">
              {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Request booking
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const PersonCard = ({ p, onBook, canViewResume }) => {
  const Icon = ROLE_ICONS[p.role] || Briefcase;
  const canBook = ['industry_expert', 'faculty'].includes(p.role);
  const hasResume = ['industry_expert', 'faculty'].includes(p.role) && p.resume_filename;
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5 hover:border-amber-300/30 transition-colors" data-testid={`person-card-${p.user_id}`}>
      <div className="flex items-start gap-3 mb-3">
        {p.picture ? (
          <img src={p.picture} alt="" className="w-11 h-11 rounded-full ring-1 ring-white/20" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-amber-300/20 text-amber-200 flex items-center justify-center font-bold ring-1 ring-white/20">
            {(p.name || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-serif font-semibold text-white truncate">{p.name}</div>
            <VerificationBadge status={p.verification_status} />
            <RatingChip userId={p.user_id} />
          </div>
          <div className="text-[10px] tracking-wider uppercase text-amber-300 mt-0.5 flex items-center gap-1">
            <Icon className="w-3 h-3" /> {roleLabel(p.role)}
          </div>
        </div>
      </div>
      {p.headline && <div className="text-[13px] text-white/85 mb-2 line-clamp-2">{p.headline}</div>}
      {(p.institution || p.industry_org_name) && (
        <div className="text-[11px] text-white/50 flex items-center gap-1 mb-1"><Building2 className="w-3 h-3" /> {p.institution || p.industry_org_name}</div>
      )}
      {p.location && <div className="text-[11px] text-white/50 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location}</div>}
      {p.engagement_routes && p.engagement_routes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {p.engagement_routes.map((r) => (
            <span key={r} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[9px] uppercase tracking-wider text-white/70">
              {(ENGAGEMENT_ROUTES.find((x) => x.id === r) || {}).label || r}
            </span>
          ))}
        </div>
      )}
      {canBook && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => onBook(p)} data-testid={`book-slot-${p.user_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-amber-300/30 text-amber-200 hover:bg-amber-300/10 text-xs font-semibold">
            <Calendar className="w-3 h-3" /> Book a 1:1
          </button>
          {hasResume && canViewResume && (
            <a href={popResumeDownloadUrl(p.user_id)} target="_blank" rel="noopener noreferrer"
              data-testid={`view-resume-${p.user_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-400/10 text-xs font-semibold">
              <FileText className="w-3 h-3" /> View resume
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const PoPDirectory = () => {
  const { user } = usePoPAuth();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [route, setRoute] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookExpert, setBookExpert] = useState(null);
  const canViewResume = user && ['university', 'faculty', 'super_admin'].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      if (route) params.engagement_route = route;
      if (verifiedOnly) params.verified_only = true;
      const res = await popDirectory(params);
      setResults(res.results || []);
    } catch (_e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, role, route, verifiedOnly]);

  return (
    <PoPShell activeTab="directory">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">DIRECTORY</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-2">Discover the confluence.</h1>
        <p className="text-sm text-white/60 max-w-2xl">Search verified Professors of Practice, universities, faculty, students and industry organisations across the ecosystem.</p>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4 mb-6 grid md:grid-cols-[1fr,220px,220px,auto] gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, headline, institution…"
            data-testid="dir-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300"
          />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="dir-role"
          className="px-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300">
          <option value="">All roles</option>
          {ROLES.map((r) => (<option key={r.id} value={r.id} className="bg-slate-900">{r.label}</option>))}
        </select>
        <select value={route} onChange={(e) => setRoute(e.target.value)} data-testid="dir-route"
          className="px-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300">
          <option value="">All engagement routes</option>
          {ENGAGEMENT_ROUTES.map((r) => (<option key={r.id} value={r.id} className="bg-slate-900">{r.label}</option>))}
        </select>
        <label className="inline-flex items-center gap-2 text-xs text-white/70 px-2">
          <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)}
            data-testid="dir-verified-only" className="accent-amber-300" />
          Verified only
        </label>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading directory…</div>
      ) : results.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
          <Users className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/60">No members yet in this filter. Be the first to join!</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((p) => (<PersonCard key={p.user_id} p={p} onBook={setBookExpert} canViewResume={canViewResume} />))}
        </div>
      )}
      {bookExpert && user && (
        <BookSlotModal expert={bookExpert} onClose={() => setBookExpert(null)} onBooked={() => {}} />
      )}
      {bookExpert && !user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setBookExpert(null)}>
          <div className="max-w-sm w-full rounded-2xl border border-white/10 bg-slate-950 p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-serif font-semibold text-white mb-2">Sign in to book</div>
            <div className="text-xs text-white/60 mb-4">Booking a 1:1 session requires a signed-in account.</div>
            <button onClick={() => setBookExpert(null)} className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/80">Close</button>
          </div>
        </div>
      )}
    </PoPShell>
  );
};

export default PoPDirectory;
